import { activePortalRegion } from '../config/portal-regions';
import type { SharedSecret } from '../utils/secret-sharing';
/* eslint-disable @typescript-eslint/no-explicit-any */
export interface EmailActivitySummary {
  window: { startDate: string; endDate: string; timezone: string; endExclusive: boolean };
  metrics: Record<string, number | null>;
  rates: Record<string, number | null>;
  lastUpdated: string | null;
  aggregation: { granularity: string; approximate: boolean; startDate: string; endDate: string; scope: string; accountTypes: string[]; complete: boolean };
  bounces: {
    available: boolean;
    issueType: string;
    pageSizePerCategory: number;
    maxRowsPerSendingType: number;
    suppressionStatusComplete: boolean;
    reason?: string;
    items: Array<{ emailId?: string; accountType: string; eventType?: string; recipientEmail: string | null; timestamp: string | null; bounceType: string | null; bounceSubType: string | null; reason: string | null; suppressionStatus: string; recommendedAction: string; resolutionSuggestion?: string }>;
    pages: Record<string, { nextCursor: string | null; hasMore: boolean }>;
    suppressionStatusAsOf: string | null;
  };
  recommendations: string[];
  notes: string[];
  supportUrl: string;
}

export interface ScheduledEmailEventSummarySchedule {
  enabled: boolean;
  deliveryTime: string;
  timezone: string;
  frequency: 'daily' | 'weekly';
  dayOfWeek?: number;
  communicationEmail: string | null;
  updatedAt?: string | null;
  message?: string;
}

export interface APIKey {
  apiKeyId: string;
  userId: string;
  label: string;
  apiKey: string;
  accountId?: string;
  permissions: string[];
  isActive: boolean;
  domainId?: string;
  domainName?: string;
  createdAt: string;
  lastUsedAt?: string;
  type?: string; // Optional type for backward compatibility
}

export interface WebApiKey {
  label: string;
  type: string;
  permissions: string[];
  userId: string;
  apiKeyId: string;
  keyName: string;
  apiKey: string;
  accountId?: string;
  isActive: boolean;
  createdAt: string;
  lastUsedAt: string | null;
  usagePlanId: string;
  roleId?: string | null;
}

export interface RepositoryTemplate {
  templateId: string;
  name: string;
  description: string;
  subject?: string;
  content?: string;
  html?: string;
  category: string;
  tags: string[];
  userId?: string;
  isOwner?: boolean;
  usageCount: number;
  createdAt: number;
  updatedAt?: number | string;
  imageUrl?: string;
  isPublic: string | boolean;
  status?: 'generating' | 'ready' | 'failed';
  errorMessage?: string;
  chatResponse?: string;
}

export interface EmailData {
  to?: string | string[];
  cc?: string | string[];
  bcc?: string | string[];
  subject: string;
  html?: string;
  from?: string;
  template_id?: string;
  template_data?: Record<string, any>;
  tags?: Record<string, string>;
  metadata?: Record<string, any>;
}

export interface DomainDnsRecord {
  type: string;
  name: string;
  value: string;
  status?: string;
  description?: string;
  priority?: number | string;
  purpose?: string;
  canRegenerateSelector?: boolean;
  verification?: {
    status?: string;
    observedAt?: string | null;
  };
}

export interface Domain {
  mailFromDomainStatus: string;
  domainId: string;
  domain: string;
  status: 'NOT_STARTED' | 'PENDING' | 'SUCCESS' | 'FAILED' | 'DISABLED';
  verificationToken?: string;
  verificationExpiresAt?: string | null;
  verificationExpiredAt?: string | null;
  disabledReason?: string | null;
  groupId?: string;
  useCase?: 'tx' | 'mk'; // Transactional or Marketing
  useCaseId?: string; // Use case configuration ID
  dnsRecords?: DomainDnsRecord[];
  dkimEnabled?: boolean;
  spfConfigured?: boolean;
  dmarcConfigured?: boolean;
  mxConfigured?: boolean;
  dkimKeyType?: 'RSA_2048_BIT' | 'RSA_1024_BIT';
  clickTrackingEnabled?: boolean;
  clickTrackingVerified?: boolean;
  inboxEnabled?: boolean;
  inboxVerified?: boolean;
  inboxSubdomain?: string;
  createdAt: string;
  updatedAt: string;
  verification?: DomainVerificationV2;
}

export interface DomainVerificationV2 {
  configuration?: { dnsRecords?: { records?: DomainDnsRecord[] } };
  features?: {
    inboundMailbox?: { enabled?: boolean; readiness?: string; desired?: { subdomain?: string } };
    clickTracking?: { enabled?: boolean; readiness?: string; desired?: { subdomain?: string } };
  };
  overall?: { domainStatus?: string; initialVerificationStatus?: string; healthStatus?: string; sendEligibility?: string };
}

function toCompatibilityStatus(status?: string): Domain['status'] | undefined {
  if (status === 'SUCCESS' || status === 'DISABLED') return status;
  if (status === 'VERIFIED') return 'SUCCESS';
  if (status === 'FAILED') return 'FAILED';
  if (status === 'NOT_CONFIGURED') return 'NOT_STARTED';
  if (status === 'PENDING' || status === 'ERROR') return 'PENDING';
  return undefined;
}

/**
 * Projects the V2 verification state into the existing UI response shape.
 * Domain records are V2-only after the schema cutover; no V1 field is read.
 */
export function normalizeDomainVerification<T extends Domain>(domain: T): T {
  const rawV2 = domain as T & DomainVerificationV2;
  const verification = domain.verification || (
    rawV2.configuration && rawV2.features && rawV2.overall
      ? rawV2
      : undefined
  );
  if (!verification) throw new Error('Domain API response is missing V2 verification state');

  const inbox = verification.features?.inboundMailbox;
  const clickTracking = verification.features?.clickTracking;
  const isSuspended = verification.overall?.sendEligibility === 'SUSPENDED_BY_ADMIN';
  const overallStatus = toCompatibilityStatus(
    verification.overall?.domainStatus || verification.overall?.initialVerificationStatus,
  );
  const coreInitiallyVerified = verification.overall?.initialVerificationStatus === 'VERIFIED';

  return {
    ...domain,
    verification,
    status: isSuspended ? 'DISABLED' : (overallStatus || 'PENDING'),
    dkimEnabled: domain.dkimEnabled ?? coreInitiallyVerified,
    spfConfigured: domain.spfConfigured ?? coreInitiallyVerified,
    dmarcConfigured: domain.dmarcConfigured ?? coreInitiallyVerified,
    mxConfigured: domain.mxConfigured ?? coreInitiallyVerified,
    mailFromDomainStatus: domain.mailFromDomainStatus || (coreInitiallyVerified ? 'SUCCESS' : 'PENDING'),
    clickTrackingEnabled: clickTracking?.enabled === true,
    clickTrackingVerified: clickTracking?.readiness === 'VERIFIED',
    inboxEnabled: inbox?.enabled === true,
    inboxVerified: inbox?.readiness === 'VERIFIED',
    inboxSubdomain: inbox?.desired?.subdomain ?? '',
  };
}

export interface UserSurveyOption {
  optionId: string;
  label: string;
}

export interface UserSurvey {
  surveyId: string;
  title: string;
  question: string;
  description?: string;
  options: UserSurveyOption[];
  answered?: boolean;
  createdAt: string;
}

export interface SMTPCredential {
  smtpId: string;
  userId: string;
  label: string;
  username: string;
  password: string;
  server: string;
  port: number;
  encryption: 'TLS' | 'SSL';
  domainId?: string;
  domainName?: string;
  fromAddress?: string;
  isActive: boolean;
  createdAt: string;
  lastUsedAt?: string;
}

export interface TeamMember {
  userId: string;
  email: string;
  name: string;
  role: string;
  roleName?: string;
  permissions: string[];
  additionalPermissions?: AdditionalPermissionGrant[];
  status: string;
  joinedAt: string;
  archivedAt?: string;
  archivedAccessSnapshot?: {
    role: string;
    roleName?: string;
    additionalPermissions: AdditionalPermissionGrant[];
  };
}

export interface TeamInvitation {
  invitationId: string;
  workspaceId: string;
  workspaceName?: string | null;
  email: string;
  role: string;
  roleName?: string;
  status: string;
  createdAt: string;
  expiresAt: string;
  invitedBy: string;
  mailboxAddresses?: string[];
  additionalPermissions?: AdditionalPermissionGrant[];
  invitationUrl?: string;
}

export interface TeamWorkspace {
  workspaceId: string;
  name: string;
  // Legacy workspaces may omit this field; clients normalize a missing value to live.
  workspaceType?: 'live' | 'sandbox';
  role: string;
  roleName?: string;
  permissions: string[];
  status: string;
  isOwner: boolean;
  createdAt: string;
  accountId?: string;
  subscriptionId?: string;
  billingWorkspaceId?: string;
  isBillingOwner?: boolean;
}

export interface RoleDefinition {
  role: string;
  description: string;
  permissions: string[];
}

export interface Workspace {
  workspaceId: string;
  name: string;
  workspaceType?: 'live' | 'sandbox';
  email?: string | null;
  logoUrl?: string | null;
  bannerUrl?: string | null;
  colorTheme?: { primary?: string; secondary?: string; accent?: string } | null;
  payingUserId?: string;
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
  role?: string;
  roleName?: string;
  memberStatus?: string;
  subscriptionStatus?: 'active' | 'inactive' | 'none';
  subscriptionPlan?: string;
  canCreateChildWorkspace?: boolean;
  canCreateSandboxWorkspace?: boolean;
  accountId?: string;
  subscriptionId?: string;
  billingWorkspaceId?: string;
  isBillingOwner?: boolean;
  parentWorkspaceId?: string | null;
  deletionStatus?: string;
  deletionRequestedAt?: string;
}

export interface WorkspaceBranding {
  name?: string;
  email?: string;
  logoUrl?: string | null;
  bannerUrl?: string | null;
  colorTheme?: { primary?: string; secondary?: string; accent?: string } | null;
}

export interface AnalyticsPiiAllowlistEntry {
  email?: string;
  domainId?: string;
  domain?: string;
  expiresAt?: string;
  reason?: string;
  createdAt?: string;
  createdBy?: string;
}

export interface AnalyticsPiiPolicy {
  recipientAllowlist: AnalyticsPiiAllowlistEntry[];
  senderDomainAllowlist: AnalyticsPiiAllowlistEntry[];
  updatedAt?: string;
  updatedBy?: string;
}

export interface WorkspaceRole {
  role: string;
  roleId: string;
  name: string;
  description: string;
  permissions: string[];
  isBuiltIn: boolean;
  principalTypes?: string[];
  apiKeyTypes?: string[];
  status?: 'active' | 'inactive';
  createdAt?: string;
  updatedAt?: string;
  inactiveAt?: string;
  createdBy?: string;
  createdByUser?: AuditLogUser;
  servicePrincipals?: WapiServicePrincipal[];
}

export interface WapiServicePrincipal {
  apiKeyId: string;
  label: string;
  maskedKey: string;
  isActive: boolean;
  createdAt?: string;
  lastUsedAt?: string | null;
}

export interface AdditionalPermissionGrant {
  permission: string;
  grantedBy: string;
  grantedAt: string;
  expiresAt: string | null;
  reason: string | null;
  isExpired?: boolean;
}

export interface AuditLogEntry {
  workspaceId: string;
  auditId: string;
  action: string;
  actorId: string;
  targetUserId: string | null;
  targetRoleId: string | null;
  details: Record<string, unknown>;
  timestamp: string;
  actor?: AuditLogUser;
  targetUser?: AuditLogUser;
}

export interface AuditLogUser {
  userId: string;
  name?: string;
  email?: string;
}

class ApiService {
  private baseURL: string;
  private currentUserId: string | null = null;
  private currentWorkspaceId: string | null = null;
  private authTokenInflight: Promise<string | null> | null = null;
  private workspaceRevision = 0;

  constructor() {
    this.baseURL = activePortalRegion.apiUrl || import.meta.env.VITE_API_GATEWAY_URL || '';
  }

  private updateAuthenticatedUser(userId: string | null, clearTokenCache: boolean) {
    if (this.currentUserId === userId) return false;
    this.currentUserId = userId;
    this.workspaceRevision += 1;
    this.currentWorkspaceId = null;
    if (clearTokenCache) this.clearAuthTokenCache();
    try {
      sessionStorage.removeItem('selectedWorkspaceId');
      localStorage.removeItem('selectedWorkspaceId');
      if (userId) {
        this.currentWorkspaceId = sessionStorage.getItem(`selectedWorkspaceId:${userId}`);
      }
    } catch {
      // Browser storage unavailable
    }
    return true;
  }

  setAuthenticatedUser(userId: string | null) {
    this.updateAuthenticatedUser(userId, true);
  }

  setWorkspaceId(workspaceId: string | null) {
    if (this.currentWorkspaceId !== workspaceId) this.workspaceRevision += 1;
    this.currentWorkspaceId = workspaceId;
    try {
      if (!this.currentUserId) return;
      const storageKey = `selectedWorkspaceId:${this.currentUserId}`;
      if (workspaceId) {
        sessionStorage.setItem(storageKey, workspaceId);
      } else {
        sessionStorage.removeItem(storageKey);
      }
    } catch {
      // Browser storage unavailable
    }
  }

  getWorkspaceId(): string | null {
    return this.currentWorkspaceId;
  }

  /** Drop any in-flight Cognito session lookup on logout or auth failure. */
  clearAuthTokenCache() {
    this.authTokenInflight = null;
  }

  private async getAuthToken(): Promise<string | null> {
    if (this.authTokenInflight) {
      return this.authTokenInflight;
    }

    this.authTokenInflight = (async () => {
      try {
        const { fetchAuthSession } = await import('aws-amplify/auth');
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken;
        const token = idToken?.toString() || null;
        const tokenUserId = typeof idToken?.payload?.sub === 'string' ? idToken.payload.sub : null;
        const userChanged = this.updateAuthenticatedUser(tokenUserId, false);
        if (userChanged && tokenUserId) {
          window.dispatchEvent(new CustomEvent('simplysend:auth-user-changed'));
        }
        return token;
      } catch (error) {
        console.error('Failed to get auth token:', error);
        return null;
      } finally {
        this.authTokenInflight = null;
      }
    })();

    return this.authTokenInflight;
  }

  async getRequestAuth(expectedWorkspaceId?: string): Promise<{ authToken: string | null; workspaceId: string | null }> {
    if (expectedWorkspaceId !== undefined && expectedWorkspaceId !== this.currentWorkspaceId) {
      throw new DOMException('Workspace changed before the request could start', 'AbortError');
    }
    const userIdBeforeRequest = this.currentUserId;
    const workspaceId = this.currentWorkspaceId;
    const workspaceRevision = this.workspaceRevision;
    const authToken = await this.getAuthToken();
    if (userIdBeforeRequest && this.currentUserId !== userIdBeforeRequest) {
      throw new Error('Authenticated user changed in another browser tab');
    }
    if (workspaceRevision !== this.workspaceRevision) {
      throw new DOMException('Workspace changed before the request could start', 'AbortError');
    }
    return { authToken, workspaceId };
  }

  // Make makeRequest method public for use by other services
  public async makeRequest(endpoint: string, options: RequestInit = {}, expectedWorkspaceId?: string): Promise<any> {
    const url = `${this.baseURL}${endpoint}`;

    const { authToken, workspaceId } = await this.getRequestAuth(expectedWorkspaceId);

    const defaultHeaders: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(authToken ? { 'Authorization': `${authToken}` } : {}),
      ...(workspaceId ? { 'x-workspace-id': workspaceId } : {}),
    };

    const config: RequestInit = {
      ...options,
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
    };

    const pathWorkspaceId = endpoint.match(/^\/(teams|workspaces)\/([^/?#]+)/);
    const requestWorkspaceId = new Headers(config.headers).get('x-workspace-id');
    if (pathWorkspaceId && !(pathWorkspaceId[1] === 'teams' && pathWorkspaceId[2] === 'invitations')
        && requestWorkspaceId && pathWorkspaceId[2] !== requestWorkspaceId) {
      throw new DOMException('Workspace changed before the request could start', 'AbortError');
    }

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        let errorData: any = null;
        try {
          errorData = await response.json(); // Generic - accept any JSON structure
          errorMessage = (errorData as any).message ||
            (errorData as any).error?.message ||
            (errorData as any).error ||
            errorMessage;
        } catch {
          // If response is not JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        const error = new Error(errorMessage);
        (error as { statusCode?: number; data?: any }).statusCode = response.status;
        // Attach full error data for frontend to use (generic)
        if (errorData) {
          (error as { data?: any }).data = errorData;
        }
        if (response.status === 401 || /access token has been revoked|token.*revoked/i.test(errorMessage)) {
          this.clearAuthTokenCache();
          window.dispatchEvent(new CustomEvent('simplysend:reauth-required'));
        }
        throw error;
      }

      // Successful revocation and other DELETE operations may return no body.
      if (response.status === 204) return undefined;

      const result = await response.json();

      // Handle standardized response format: { success: true, data: { ... } }
      if (result && typeof result === 'object' && result.success === true && 'data' in result) {
        return result.data;
      }

      return result;
    } catch (error) {
      console.error(`API request failed for ${endpoint}:`, error);
      throw error;
    }
  }


  // Auth endpoints
  async login(email: string, password: string) {
    return this.makeRequest('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
  }

  async register(email: string, password: string, name: string) {
    return this.makeRequest('/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name }),
    });
  }

  async logout() {
    return this.makeRequest('/account/logout', {
      method: 'POST',
    });
  }

  // Email endpoints
  async sendEmail(emailData: EmailData) {
    return this.makeRequest('/emails/send', {
      method: 'POST',
      body: JSON.stringify(emailData),
    });
  }

  // Stats endpoints
  async getEmailActivitySummary(params: Record<string, string>): Promise<EmailActivitySummary> {
    return this.makeRequest(`/email-activity-summary?${new URLSearchParams(params).toString()}`);
  }

  async getScheduledEmailEventSummarySchedule(): Promise<ScheduledEmailEventSummarySchedule> {
    return this.makeRequest('/email-activity-summary/schedule');
  }

  async updateScheduledEmailEventSummarySchedule(settings: Pick<ScheduledEmailEventSummarySchedule, 'enabled'> & Partial<Pick<ScheduledEmailEventSummarySchedule, 'deliveryTime' | 'timezone' | 'frequency' | 'dayOfWeek'>>): Promise<ScheduledEmailEventSummarySchedule> {
    return this.makeRequest('/email-activity-summary/schedule', { method: 'PUT', body: JSON.stringify(settings) });
  }

  async getStats(params?: any) {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.makeRequest(`/stats${queryString}`);
  }

  async getDashboardStats(params?: any) {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.makeRequest(`/dashboard/stats${queryString}`);
  }
  async getDashboardMiniStats(params?: any) {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    const response = await this.makeRequest(`/stats${queryString}`);
    return response;
  }

  // Live Events endpoint - Real-time minute-by-minute monitoring
  async getLiveEvents(params: { accountType: 'transactional' | 'marketing'; minuteTimestamp: string }) {
    const queryString = new URLSearchParams(params).toString();
    return this.makeRequest(`/live-events?${queryString}`);
  }

  // Domain endpoints
  async getDomains(workspaceId?: string) {
    const response = await this.makeRequest('/domains', {}, workspaceId);
    return {
      ...response,
      domains: Array.isArray(response?.domains)
        ? response.domains.map((domain: Domain) => normalizeDomainVerification(domain))
        : [],
    };
  }

  async createDomain(domainData: any) {
    return this.makeRequest('/domains', {
      method: 'PUT',
      body: JSON.stringify(domainData),
    });
  }

  async deleteDomain(domainId: string) {
    return this.makeRequest(`/domains/${domainId}`, {
      method: 'DELETE',
    });
  }

  async getAvailableUseCases() {
    return this.makeRequest('/use-cases');
  }

  async reprovisionDomainDkim(domainId: string) {
    return this.makeRequest(`/domains/${domainId}/dkim/reprovision`, {
      method: 'POST',
    });
  }

  async refreshInitialDomainDns(domainId: string) {
    return this.makeRequest(`/domains/${domainId}/refresh-dns`, {
      method: 'POST',
    });
  }

  // Keep old method name for backward compatibility
  async getAvailableRegions() {
    return this.getAvailableUseCases();
  }

  async verifyDomain(domainId: string) {
    return this.makeRequest(`/domains/${domainId}/verify`, {
      method: 'POST',
    });
  }

  async getDomainStatus(domainId: string) {
    return this.makeRequest(`/domains/${domainId}/status`);
  }

  async getDNSRecords(domainId: string) {
    const response = await this.makeRequest(`/domains/${domainId}/dns-records`);
    return normalizeDomainVerification(response as Domain);
  }

  async toggleDomainClickTracking(domainId: string, clickTrackingEnabled: boolean) {
    return this.makeRequest(`/domains/${domainId}/tracking`, {
      method: 'PATCH',
      body: JSON.stringify({ enabled: clickTrackingEnabled }),
    });
  }

  async toggleDomainInbox(domainId: string, inboxEnabled: boolean, inboxSubdomain?: string) {
    return this.makeRequest(`/domains/${domainId}/inbox`, {
      method: 'PATCH',
      body: JSON.stringify({ enabled: inboxEnabled, inboxSubdomain }),
    });
  }

  async refreshOptionalDomainFeature(domainId: string, feature: 'tracking' | 'inbox') {
    return this.makeRequest(`/domains/${domainId}/${feature}/refresh`, { method: 'POST' });
  }

  async verifyOptionalDomainFeature(domainId: string, feature: 'tracking' | 'inbox') {
    return this.makeRequest(`/domains/${domainId}/${feature}/verify`, { method: 'POST' });
  }

  async detectDnsProvider(domain: string) {
    return this.makeRequest(`/domains/detect-provider?domain=${encodeURIComponent(domain)}`);
  }

  async testWebhook(webhookId: string) {
    return this.makeRequest(`/webhooks/${webhookId}/test`, {
      method: 'POST',
    });
  }

  // IP Pool endpoints
  async getIPPools() {
    return this.makeRequest('/ip-pools');
  }

  async createIPPool(poolData: any) {
    return this.makeRequest('/ip-pools', {
      method: 'POST',
      body: JSON.stringify(poolData),
    });
  }

  async deleteIPPool(poolId: string) {
    return this.makeRequest(`/ip-pools/${poolId}`, {
      method: 'DELETE',
    });
  }

  async assignDomainsToIPPool(poolId: string, domainIds: string[]) {
    return this.makeRequest(`/ip-pools/${poolId}/assign`, {
      method: 'POST',
      body: JSON.stringify({ domainIds }),
    });
  }

  async unassignDomainFromIPPool(poolId: string, domainId: string) {
    return this.makeRequest(`/ip-pools/${poolId}/unassign`, {
      method: 'POST',
      body: JSON.stringify({ domainId }),
    });
  }

  // Webhooks API
  async getWebhooks(params?: any) {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.makeRequest(`/webhooks${queryString}`);
  }

  async revealWebhookSecret(webhookId: string, workspaceId: string) {
    return this.makeRequest(`/webhooks/${encodeURIComponent(webhookId)}/secret`, { method: 'POST', cache: 'no-store' }, workspaceId);
  }

  async createWebhook(webhookData: any) {
    return this.makeRequest('/webhooks', {
      method: 'POST',
      body: JSON.stringify(webhookData),
    });
  }

  async updateWebhook(webhookId: string, webhookData: any) {
    return this.makeRequest(`/webhooks/${webhookId}`, {
      method: 'PUT',
      body: JSON.stringify(webhookData),
    });
  }

  async deleteWebhook(webhookId: string) {
    return this.makeRequest(`/webhooks/${webhookId}`, {
      method: 'DELETE',
    });
  }

  // Inbound Emails endpoints
  async getInboundEmails(params: { domainId: string; mailboxAddress?: string; limit?: number; nextKey?: string; includeHidden?: boolean; hiddenOnly?: boolean }) {
    const cleanParams = Object.fromEntries(
      Object.entries(params)
        .filter(([, v]) => v != null)
        .map(([k, v]) => [k, String(v)])
    );
    const queryString = `?${new URLSearchParams(cleanParams).toString()}`;
    return this.makeRequest(`/web-setup/inbound-emails${queryString}`);
  }

  async getInboundEmailDetails(params: { domainId: string; timestamp: number; attachmentIndex?: number; includeHidden?: boolean; hiddenOnly?: boolean; includeOriginal?: boolean; webhookStatusOnly?: boolean }) {
    const cleanParams = Object.fromEntries(
      Object.entries(params)
        .filter(([, v]) => v != null)
        .map(([k, v]) => [k, String(v)])
    );
    const queryString = `?${new URLSearchParams(cleanParams).toString()}`;
    return this.makeRequest(`/web-setup/inbound-emails${queryString}`);
  }

  async patchInboundEmailStatus(params: { domainId: string; timestamp: number }, data: { isRead?: boolean; isHidden?: boolean }) {
    const queryString = `?domainId=${encodeURIComponent(params.domainId)}&timestamp=${params.timestamp}`;
    return this.makeRequest(`/web-setup/inbound-emails${queryString}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async patchInboundEmailsBulk(
    domainId: string,
    items: Array<{ timestamp: number }>,
    data: { isRead?: boolean; isHidden?: boolean },
  ) {
    const queryString = `?domainId=${encodeURIComponent(domainId)}`;
    return this.makeRequest(`/web-setup/inbound-emails${queryString}`, {
      method: 'PATCH',
      body: JSON.stringify({ items, ...data }),
    });
  }

  async moveAllMailboxInboxToTrash(params: { domainId: string; mailboxAddress: string }): Promise<{
    success: boolean;
    queued: boolean;
  }> {
    const queryString = new URLSearchParams(params).toString();
    return this.makeRequest(`/web-setup/inbound-emails?${queryString}`, {
      method: 'PATCH',
      body: JSON.stringify({ moveAllToTrash: true }),
    });
  }

  async deleteInboundEmail(params: { domainId: string; timestamp: number }) {
    const queryString = `?domainId=${encodeURIComponent(params.domainId)}&timestamp=${params.timestamp}`;
    return this.makeRequest(`/web-setup/inbound-emails${queryString}`, {
      method: 'DELETE',
    });
  }

  async deleteAllMailboxTrash(params: { domainId: string; mailboxAddress: string }): Promise<{
    success: boolean;
    queued: boolean;
  }> {
    const queryString = new URLSearchParams({
      domainId: params.domainId,
      mailboxAddress: params.mailboxAddress,
      purgeAll: 'true',
    }).toString();
    return this.makeRequest(`/web-setup/inbound-emails?${queryString}`, {
      method: 'DELETE',
    });
  }

  async deleteSelectedMailboxTrash(params: { domainId: string; mailboxAddress: string; timestamps: number[] }): Promise<{
    success: boolean;
    queued: boolean;
  }> {
    const queryString = new URLSearchParams({
      domainId: params.domainId,
      mailboxAddress: params.mailboxAddress,
      purgeSelected: 'true',
    }).toString();
    return this.makeRequest(`/web-setup/inbound-emails?${queryString}`, {
      method: 'DELETE',
      body: JSON.stringify({ items: params.timestamps.map((timestamp) => ({ timestamp })) }),
    });
  }

  async getMailboxSentItems(params: {
    domainId: string;
    mailboxAddress: string;
    limit?: number;
    nextKey?: string;
    timestamp?: number;
    attachmentIndex?: number;
  }) {
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([, value]) => value != null).map(([key, value]) => [key, String(value)]),
    );
    return this.makeRequest(`/web-setup/mailbox-sent-items?${new URLSearchParams(cleanParams).toString()}`);
  }

  async deleteMailboxSentItem(params: { domainId: string; mailboxAddress: string; timestamp: number }) {
    const cleanParams = Object.fromEntries(
      Object.entries(params).filter(([, value]) => value != null).map(([key, value]) => [key, String(value)]),
    );
    return this.makeRequest(`/web-setup/mailbox-sent-items?${new URLSearchParams(cleanParams).toString()}`, {
      method: 'DELETE',
    });
  }

  async getMailboxThread(params: { domainId: string; mailboxAddress: string; threadId: string }) {
    const query = new URLSearchParams(params).toString();
    return this.makeRequest(`/web-setup/mailbox-thread?${query}`);
  }

  async searchMailbox(params: {
    domainId: string;
    mailboxAddress: string;
    q: string;
    folder: 'inbox' | 'trash' | 'sent';
  }) {
    return this.makeRequest(`/web-setup/mailbox-search?${new URLSearchParams(params).toString()}`);
  }

  async getMailboxDrafts(): Promise<{ success: boolean; drafts: Array<Record<string, any>> }> {
    return this.makeRequest('/web-setup/mailbox-drafts');
  }

  async getMailboxDraft(draftId: string): Promise<{ success: boolean; draft: Record<string, any> }> {
    return this.makeRequest(`/web-setup/mailbox-drafts?draftId=${encodeURIComponent(draftId)}`);
  }

  async saveMailboxDraft(data: {
    draftId?: string;
    mailboxAddress: string;
    to: string;
    cc?: string;
    bcc?: string;
    subject: string;
    html: string;
    replyTo?: string;
    attachments?: Array<{ name: string; type: string; size: number; content: string }>;
    threadId?: string;
    inReplyTo?: string;
    references?: string[];
  }) {
    const query = data.draftId ? `?draftId=${encodeURIComponent(data.draftId)}` : '';
    return this.makeRequest(`/web-setup/mailbox-drafts${query}`, {
      method: data.draftId ? 'PUT' : 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteMailboxDraft(draftId: string): Promise<{ success: boolean }> {
    return this.makeRequest(`/web-setup/mailbox-drafts?draftId=${encodeURIComponent(draftId)}`, {
      method: 'DELETE',
    });
  }

  async getWorkspaceConfigurations(workspaceId: string): Promise<{ configs: Record<string, { value: boolean; description: string }> }> {
    return this.makeRequest('/account/configurations', {}, workspaceId);
  }

  async setWorkspaceConfiguration(configKey: string, value: boolean, workspaceId: string): Promise<void> {
    await this.makeRequest('/account/configurations', {
      method: 'PUT',
      body: JSON.stringify({ configKey, value }),
    }, workspaceId);
  }

  // User Profile endpoints
  async getUserSettings(workspaceId?: string) {
    return this.makeRequest(`/users/settings`, {}, workspaceId);
  }

  async updateUserSettings(settingsData: any) {
    return this.makeRequest(`/users/settings`, {
      method: 'PUT',
      body: JSON.stringify(settingsData),
    });
  }

  async requestCommunicationEmailVerification(email: string) {
    return this.makeRequest('/account/communication-email/request', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async verifyCommunicationEmail(code: string) {
    return this.makeRequest('/account/communication-email/verify', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  // API Key endpoints
  async updateApiKeyLabel(keyId: string, label: string, workspaceId?: string): Promise<{ apiKeyId: string; label: string }> {
    return this.makeRequest(`/all-api-keys/${encodeURIComponent(keyId)}/label`, { method: 'PUT', body: JSON.stringify({ label }) }, workspaceId);
  }

  async getAllApiKeys(workspaceId: string | undefined, types: string[]): Promise<{ apiKeys: APIKey[]; count: number }> {
    return this.makeRequest(`/all-api-keys?types=${encodeURIComponent(types.join(','))}`, {}, workspaceId);
  }

  async getApiKeys(workspaceId?: string) {
    return this.makeRequest('/api-keys', {}, workspaceId);
  }

  async getMarketingApiKeys(workspaceId?: string) {
    return this.makeRequest('/marketing-api-keys', {}, workspaceId);
  }

  async createApiKey(keyData: any) {
    return this.makeRequest('/all-api-keys', {
      method: 'POST',
      body: JSON.stringify({ ...keyData, type: 'transactional' }),
    });
  }

  async createMarketingApiKey(keyData: any) {
    return this.makeRequest('/all-api-keys', {
      method: 'POST',
      body: JSON.stringify({ ...keyData, type: 'marketing' }),
    });
  }

  async deleteApiKey(keyId: string) {
    return this.makeRequest(`/all-api-keys/${encodeURIComponent(keyId)}`, {
      method: 'DELETE',
    });
  }

  async deleteMarketingApiKey(keyId: string) {
    return this.makeRequest(`/all-api-keys/${encodeURIComponent(keyId)}`, {
      method: 'DELETE',
    });
  }

  // Domain Groups API Key endpoints
  async createWebSetupApiKey(keyData: any) {
    return this.makeRequest('/all-api-keys', {
      method: 'POST',
      body: JSON.stringify({ ...keyData, type: 'web-setup' }),
    });
  }

  async getWebSetupApiKeys(workspaceId?: string) {
    // Use the same endpoint as email API keys - filtering will be done on frontend
    return this.makeRequest('/web-setup-api-keys', {}, workspaceId);
  }

  async deleteWebSetupApiKey(keyId: string) {
    return this.makeRequest(`/all-api-keys/${encodeURIComponent(keyId)}`, {
      method: 'DELETE',
    });
  }

  async getSecretShares(workspaceId: string, cursor?: string | null): Promise<{ shares: SharedSecret[]; cursor: string | null }> {
    return this.makeRequest(`/shared-secrets${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`, {}, workspaceId);
  }

  async createSecretShare(input: { label: string; secret: string; expiresInHours: number; recipientEmail?: string; source?: { kind: string; id: string } }, workspaceId: string): Promise<SharedSecret> {
    return this.makeRequest('/shared-secrets', { method: 'POST', body: JSON.stringify(input), cache: 'no-store' }, workspaceId);
  }

  async emailSecretShare(shareId: string, input: { token: string; recipientEmail: string }, workspaceId: string): Promise<SharedSecret> {
    return this.makeRequest(`/shared-secrets/${encodeURIComponent(shareId)}/email`, { method: 'POST', body: JSON.stringify(input), cache: 'no-store' }, workspaceId);
  }

  async revokeSecretShare(shareId: string, workspaceId: string) {
    return this.makeRequest(`/shared-secrets/${encodeURIComponent(shareId)}`, { method: 'DELETE' }, workspaceId);
  }

  // SMTP endpoints
  async getSMTPCredentials(workspaceId?: string) {
    return this.makeRequest('/smtp', {}, workspaceId);
  }

  async getSmtpConfig(workspaceId?: string): Promise<{ available: boolean; fallbackCharsetSupported: boolean; smtpWildcardFromAllowed: boolean; shortPasswordSupported: boolean; standardPasswordLength: number }> {
    return this.makeRequest('/smtp/config', {}, workspaceId);
  }

  async createSMTPCredential(smtpData: any) {
    return this.makeRequest('/smtp', {
      method: 'POST',
      body: JSON.stringify(smtpData),
    });
  }

  async deleteSMTPCredential(credentialId: string) {
    return this.makeRequest(`/smtp/${credentialId}`, {
      method: 'DELETE',
    });
  }

  async updateSMTPCredentialLabel(credentialId: string, label: string, workspaceId?: string): Promise<{ smtpId: string; label: string; updatedAt?: string }> {
    return this.makeRequest(`/smtp/${encodeURIComponent(credentialId)}/label`, {
      method: 'PUT',
      body: JSON.stringify({ label }),
    }, workspaceId);
  }

  async updateSMTPCredentialSettings(credentialId: string, settings: { label: string; headerFallbackCharset?: string }, workspaceId?: string): Promise<{ smtpId: string; label: string; headerFallbackCharset?: string; updatedAt?: string }> {
    return this.makeRequest(`/smtp/${encodeURIComponent(credentialId)}/settings`, {
      method: 'PUT',
      body: JSON.stringify(settings),
    }, workspaceId);
  }

  // Limits endpoints
  async getLimits() {
    return this.makeRequest('/limits');
  }

  async getResourceLimits(resource: 'shared-secrets' | 'domains' | 'smtp' | 'supabase' | 'webhooks' | 'api-keys' | 'contacts' | 'custom-fields' | 'subscription-groups' | 'segments' | 'campaigns' | 'workflows' | 'user-templates' | 'ai-templates', workspaceId?: string) {
    return this.makeRequest(`/limits/${resource}`, resource === 'shared-secrets' ? { cache: 'no-store' } : {}, workspaceId);
  }

  async checkResourceLimit(resourceType: string) {
    return this.makeRequest(`/limits/check/${resourceType}`);
  }

  // SMTP request endpoints
  async createSMTPRequest(reason: string) {
    return this.makeRequest('/smtp-request', {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async getSMTPRequestStatus() {
    return this.makeRequest('/smtp-request/status');
  }

  // Template endpoints
  async getTemplates() {
    return this.makeRequest('/templates');
  }

  async getTemplate(templateId: string, options?: RequestInit) {
    return this.makeRequest(`/templates/${templateId}`, options);
  }

  async createTemplate(templateData: any) {
    return this.makeRequest('/templates', {
      method: 'POST',
      body: JSON.stringify(templateData),
    });
  }

  async updateTemplate(templateId: string, templateData: any) {
    return this.makeRequest(`/templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(templateData),
    });
  }

  async deleteTemplate(templateId: string) {
    return this.makeRequest(`/templates/${templateId}`, {
      method: 'DELETE',
    });
  }

  async approveTemplate(templateId: string) {
    return this.makeRequest(`/templates/${templateId}/approve`, { method: 'POST' });
  }

  // Template Repository endpoints
  async createAITemplate(templateData: any): Promise<RepositoryTemplate> {
    return this.makeRequest('/template-repository/ai-create', {
      method: 'POST',
      body: JSON.stringify(templateData),
    });
  }

  async getRepositoryTemplates(params?: any) {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.makeRequest(`/template-repository${queryString}`);
  }

  async cloneRepositoryTemplate(templateId: string) {
    return this.makeRequest(`/template-repository/${templateId}`, {
      method: 'POST',
    });
  }

  async getRepositoryTemplate(templateId: string, options?: RequestInit): Promise<RepositoryTemplate> {
    return this.makeRequest(`/template-repository/${templateId}`, options);
  }

  async updateTemplateVisibility(templateId: string, isPublic: boolean) {
    return this.makeRequest(`/template-repository/${templateId}`, {
      method: 'PATCH',
      body: JSON.stringify({ isPublic }),
    });
  }

  async deleteRepositoryTemplate(templateId: string) {
    return this.makeRequest(`/template-repository/${templateId}`, {
      method: 'DELETE',
    });
  }

  async uploadPreviewImage(previewData: { templateId: string; imageData: string }) {
    return this.makeRequest('/preview-upload', {
      method: 'POST',
      body: JSON.stringify(previewData),
    });
  }

  // Campaign endpoints
  async getCampaigns() {
    return this.makeRequest('/campaigns');
  }

  async createCampaign(campaignData: any) {
    return this.makeRequest('/campaigns', {
      method: 'POST',
      body: JSON.stringify(campaignData),
    });
  }

  async getCampaign(campaignId: string) {
    return this.makeRequest(`/campaigns/${campaignId}`);
  }

  async updateCampaign(campaignId: string, campaignData: any) {
    return this.makeRequest(`/campaigns/${campaignId}`, {
      method: 'PUT',
      body: JSON.stringify(campaignData),
    });
  }

  async moveCampaignToDraft(campaignId: string) {
    return this.updateCampaign(campaignId, { approvalStatus: 'draft' });
  }

  async deleteCampaign(campaignId: string) {
    return this.makeRequest(`/campaigns/${campaignId}`, {
      method: 'DELETE',
    });
  }

  async scheduleCampaign(campaignId: string) {
    return this.makeRequest(`/campaigns/${campaignId}/schedule`, {
      method: 'POST',
    });
  }

  async approveCampaign(campaignId: string) {
    return this.makeRequest(`/campaigns/${campaignId}/approve`, { method: 'POST' });
  }

  async submitCampaignForApproval(campaignId: string) {
    return this.makeRequest(`/campaigns/${campaignId}/submit-approval`, { method: 'POST' });
  }

  async getCampaignEvents(campaignId: string, params?: { limit?: number; cursor?: string }) {
    const queryParts: string[] = [];
    if (params?.limit) queryParts.push(`limit=${params.limit}`);
    if (params?.cursor) queryParts.push(`cursor=${encodeURIComponent(params.cursor)}`);
    const queryString = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
    return this.makeRequest(`/campaigns/${campaignId}/events${queryString}`);
  }


  // Settings endpoints
  async getSettings() {
    return this.makeRequest('/settings');
  }

  async updateSettings(settingsData: any) {
    return this.makeRequest('/settings', {
      method: 'PUT',
      body: JSON.stringify(settingsData),
    });
  }

  // Domain Groups endpoints
  async createDomainGroup(groupData: any) {
    return this.makeRequest('/domain-groups', {
      method: 'POST',
      body: JSON.stringify(groupData),
    });
  }

  async getDomainGroups() {
    return this.makeRequest('/domain-groups');
  }

  async updateDomainGroup(groupId: string, groupData: any) {
    return this.makeRequest(`/domain-groups/${groupId}`, {
      method: 'PUT',
      body: JSON.stringify(groupData),
    });
  }

  async deleteDomainGroup(groupId: string) {
    return this.makeRequest(`/domain-groups/${groupId}`, {
      method: 'DELETE',
    });
  }

  async assignDomainToGroup(domainId: string, groupId: string) {
    return this.makeRequest(`/domains/${domainId}/group`, {
      method: 'PUT',
      body: JSON.stringify({ groupId }),
    });
  }

  async removeDomainFromGroup(domainId: string) {
    return this.makeRequest(`/domains/${domainId}/group`, {
      method: 'PUT',
      body: JSON.stringify({ groupId: null }),
    });
  }

  async assignDomainToGroupOld(domainId: string, groupId: string) {
    return this.makeRequest(`/domain-groups/${groupId}/domains/${domainId}`, {
      method: 'POST',
    });
  }

  async removeDomainFromGroupOld(domainId: string, groupId: string) {
    return this.makeRequest(`/domain-groups/${groupId}/domains/${domainId}`, {
      method: 'DELETE',
    });
  }

  // Auth endpoints
  async changePassword(passwordData: any) {
    return this.makeRequest('/auth/change-password', {
      method: 'PUT',
      body: JSON.stringify(passwordData),
    });
  }

  // Billing endpoints
  async getBillingInfo() {
    return this.makeRequest('/billing');
  }

  async getUsage(workspaceId?: string) {
    return this.makeRequest('/billing/usage', {}, workspaceId);
  }

  async updateBillingInfo(billingData: any) {
    return this.makeRequest('/billing', {
      method: 'PUT',
      body: JSON.stringify(billingData),
    });
  }

  async getEmailCredits(cursor?: string, workspaceId?: string) {
    const queryString = cursor ? `?${new URLSearchParams({ cursor }).toString()}` : '';
    return this.makeRequest(`/billing/credits${queryString}`, {}, workspaceId);
  }

  async createCustomCreditCheckoutSession(data: { offerId: string; successUrl: string; cancelUrl: string }) {
    return this.makeRequest('/billing/credits/custom-checkout', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Compliance Template endpoints
  async getComplianceTemplates(workspaceId?: string) {
    return this.makeRequest('/compliance-templates', {}, workspaceId);
  }

  async getComplianceTemplate(templateId: string) {
    return this.makeRequest(`/compliance-templates/${templateId}`);
  }

  async createComplianceTemplate(templateData: any) {
    return this.makeRequest('/compliance-templates', {
      method: 'POST',
      body: JSON.stringify(templateData),
    });
  }

  async updateComplianceTemplate(templateId: string, templateData: any) {
    return this.makeRequest(`/compliance-templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(templateData),
    });
  }

  async deleteComplianceTemplate(templateId: string) {
    return this.makeRequest(`/compliance-templates/${templateId}`, {
      method: 'DELETE',
    });
  }

  // User Compliance Approval endpoints
  async getApprovalRequests(params?: any) {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.makeRequest(`/approval-requests${queryString}`);
  }

  async getApprovalRequest(approvalType: string) {
    return this.makeRequest(`/approval-requests/${encodeURIComponent(approvalType)}`);
  }

  async createApprovalRequest(approvalData: { approvalType: string; justification: string; examples?: string[]; userNote?: string }) {
    return this.makeRequest('/approval-requests', {
      method: 'POST',
      body: JSON.stringify(approvalData),
    });
  }

  async updateApprovalRequest(approvalType: string, approvalData: { justification: string; examples?: string[]; userNote?: string }) {
    return this.makeRequest(`/approval-requests/${encodeURIComponent(approvalType)}`, {
      method: 'PUT',
      body: JSON.stringify(approvalData),
    });
  }

  // User Account Activities endpoints
  async verifySignInCredentials(
    password: string,
    totpCode?: string,
  ) {
    return this.makeRequest('/account/verify-sign-in', {
      method: 'POST',
      body: JSON.stringify({ method: 'password', password, totpCode }),
    });
  }

  async verifySignInPasskeyStart() {
    return this.makeRequest('/account/verify-sign-in', {
      method: 'POST',
      body: JSON.stringify({ method: 'passkey', step: 'start' }),
    });
  }

  async verifySignInPasskeyComplete(credential: Record<string, unknown>) {
    return this.makeRequest('/account/verify-sign-in', {
      method: 'POST',
      body: JSON.stringify({ method: 'passkey', step: 'complete', credential }),
    });
  }

  async logSignInMethodChanged(previousMethod: 'none' | 'totp' | 'passkey', newMethod: 'none' | 'totp' | 'passkey') {
    return this.makeRequest('/account/sign-in-method-changed', {
      method: 'POST',
      body: JSON.stringify({ previousMethod, newMethod }),
    });
  }

  async updatePasskeyOnlyState(
    action: 'activate' | 'deactivate' | 'restore-password',
    previousMethod?: 'none' | 'totp',
    stepUpToken?: string,
    newPassword?: string,
  ) {
    return this.makeRequest('/account/passkey-only', {
      method: 'POST',
      body: JSON.stringify({ action, previousMethod, stepUpToken, newPassword }),
    });
  }

  async getAccountActivities(params?: any) {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.makeRequest(`/account-activities${queryString}`);
  }

  // Survey endpoints
  async submitOnboardingSurvey(onboardingSurveyData: { useCase: string; emailVolume: string; contactVolume?: string; source: string }) {
    return this.makeRequest('/onboarding-survey', {
      method: 'POST',
      body: JSON.stringify(onboardingSurveyData),
    });
  }

  async getSurveys(includeAnswered = false): Promise<{ surveys: UserSurvey[]; count: number }> {
    const query = includeAnswered ? '?all=true' : '';
    return this.makeRequest(`/surveys${query}`);
  }

  async respondToSurvey(surveyId: string, optionId: string, otherText?: string) {
    return this.makeRequest(`/surveys/${surveyId}/respond`, {
      method: 'POST',
      body: JSON.stringify({ optionId, otherText }),
    });
  }

  async reportIssue(details: {
    title?: string;
    url: string;
    accountId: string;
    userEmail: string;
    timestamp: string;
    userAgent: string;
    language: string;
    screenResolution: string;
    errorMessage: string;
    stackTrace: string;
    additionalDetails?: string;
    screenshot?: string;
    screenshots?: string[];
    consoleLogs?: string;
    severity?: 'critical' | 'error' | 'warning';
  }) {
    return this.makeRequest('/report-issue', {
      method: 'POST',
      body: JSON.stringify(details),
    });
  }

  async pageSupport(details: {
    title: string;
    url: string;
    accountId: string;
    userEmail: string;
    timestamp: string;
    userAgent: string;
    language: string;
    screenResolution: string;
    errorMessage: string;
    stackTrace: string;
    additionalDetails?: string;
    screenshots?: string[];
    consoleLogs?: string;
  }): Promise<{ success: boolean; messageId?: string; pageSubmitted: boolean; emailSent: boolean; incidentId?: string }> {
    return this.makeRequest('/support/page', {
      method: 'POST',
      body: JSON.stringify(details),
    });
  }

  async getIncidentReports(): Promise<{
    reports: Array<{
      id?: string;
      incidentId?: string;
      title?: string;
      severity: 'sev1' | 'sev2' | 'sev3' | 'sev4' | 'sev5';
      status: 'open' | 'acknowledged';
      submittedAt: string;
      acknowledgedAt?: string | null;
    }>
  }> {
    return this.makeRequest('/support/incidents');
  }

  // Email List endpoints (for user-facing email list page)
  async getEmailEvents(params?: any) {
    const cleanParams: Record<string, string> = params
      ? Object.fromEntries(
        Object.entries(params)
          .filter(([, v]) => v != null)
          .map(([k, v]) => [k, String(v)])
      )
      : {};
    if (cleanParams.searchType === 'email') {
      return this.makeRequest('/email-events/search', { method: 'POST', body: JSON.stringify(cleanParams) });
    }
    const queryString = params ? `?${new URLSearchParams(cleanParams).toString()}` : '';
    return this.makeRequest(`/email-events${queryString}`);
  }

  async getEmailDetails(params?: any) {
    const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
    return this.makeRequest(`/email-events/details${queryString}`);
  }

  async updateEmailAnnotation(data: { emailId: string; annotation: string; accountType: string }) {
    return this.makeRequest('/email-events/annotation', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async exportEmailEvents(data: {
    accountType: 'transactional' | 'marketing';
    startDate: string;
    endDate: string;
    eventTypes: string[];
    columns?: string[];
    label?: string;
  }) {
    return this.makeRequest('/email-events/exports', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async downloadEmailEventExport(jobId: string) {
    return this.makeRequest(`/email-events/exports/${jobId}/download`);
  }

  // BIMI endpoints
  async getBimiRecord(domainId: string) {
    const response = await this.makeRequest(`/domains/${domainId}/bimi`);
    return response.data || response; // Handle both new and old response formats
  }

  async saveBimiRecord(domainId: string, bimiData: { logoUrl: string; vmcUrl?: string }) {
    const response = await this.makeRequest(`/domains/${domainId}/bimi`, {
      method: 'POST',
      body: JSON.stringify(bimiData),
    });
    return response.data || response; // Handle both new and old response formats
  }

  async verifyBimiRecord(domainId: string) {
    const response = await this.makeRequest(`/domains/${domainId}/bimi/verify`, {
      method: 'POST',
    });
    return response.data || response; // Handle both new and old response formats
  }

  // Suppression List endpoints
  async addSuppressions(emails: string[], workspaceId: string): Promise<{ added: number; existing: number; failed: string[] }> {
    return this.makeRequest('/suppressions', {
      method: 'POST',
      body: JSON.stringify({ emails }),
    }, workspaceId);
  }

  async getSuppressions(params?: { limit?: number; cursor?: string; search?: string }) {
    const queryParts: string[] = [];
    if (params?.limit) queryParts.push(`limit=${params.limit}`);
    if (params?.cursor) queryParts.push(`cursor=${encodeURIComponent(params.cursor)}`);
    if (params?.search) queryParts.push(`search=${encodeURIComponent(params.search)}`);
    const queryString = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
    return this.makeRequest(`/suppressions${queryString}`);
  }

  async checkSuppression(email: string) {
    return this.makeRequest(`/suppressions/check?email=${encodeURIComponent(email)}`);
  }

  async removeSuppression(email: string, suppressionKey?: string) {
    const query = suppressionKey ? `?suppressionKey=${encodeURIComponent(suppressionKey)}` : '';
    return this.makeRequest(`/suppressions/${encodeURIComponent(email)}${query}`, {
      method: 'DELETE',
    });
  }

  async reactivateSuppression(email: string, suppressionKey: string) {
    return this.makeRequest('/suppressions/reactivate', {
      method: 'POST',
      body: JSON.stringify({ email, suppressionKey }),
    });
  }

  // GDPR Account Deletion
  async deleteAccount(): Promise<{ success: boolean; message: string; scheduledFor?: string }> {
    return this.makeRequest('/account', {
      method: 'DELETE',
    });
  }

  async cancelAccountDeletion(): Promise<{ success: boolean; message: string }> {
    return this.makeRequest('/account/cancel-deletion', {
      method: 'POST',
    });
  }

  // ─── Workflow endpoints ────────────────────────────────────────────────────

  async getWorkflows(params?: { status?: string; limit?: number }) {
    const queryString = params ? `?${new URLSearchParams(params as Record<string, string>).toString()}` : '';
    return this.makeRequest(`/workflows${queryString}`);
  }

  async getWorkflow(workflowId: string) {
    return this.makeRequest(`/workflows/${workflowId}`);
  }

  async createWorkflow(workflowData: any) {
    return this.makeRequest('/workflows', {
      method: 'POST',
      body: JSON.stringify(workflowData),
    });
  }

  async updateWorkflow(workflowId: string, workflowData: any) {
    return this.makeRequest(`/workflows/${workflowId}`, {
      method: 'PUT',
      body: JSON.stringify(workflowData),
    });
  }

  async deleteWorkflow(workflowId: string) {
    return this.makeRequest(`/workflows/${workflowId}`, {
      method: 'DELETE',
    });
  }

  async activateWorkflow(workflowId: string) {
    return this.makeRequest(`/workflows/${workflowId}/activate`, {
      method: 'PUT',
    });
  }

  async approveWorkflow(workflowId: string) {
    return this.makeRequest(`/workflows/${workflowId}/approve`, { method: 'POST' });
  }

  async pauseWorkflow(workflowId: string) {
    return this.makeRequest(`/workflows/${workflowId}/pause`, {
      method: 'PUT',
    });
  }

  async archiveWorkflow(workflowId: string, terminationMode: 'drain' | 'abort' = 'drain') {
    return this.makeRequest(`/workflows/${workflowId}/archive`, {
      method: 'PUT',
      body: JSON.stringify({ terminationMode }),
    });
  }

  async restoreWorkflow(workflowId: string) {
    return this.makeRequest(`/workflows/${workflowId}/restore`, {
      method: 'PUT',
    });
  }

  async getWorkflowExecutions(workflowId: string, params?: { limit?: number; cursor?: string }) {
    const queryParts: string[] = [];
    if (params?.limit) queryParts.push(`limit=${params.limit}`);
    if (params?.cursor) queryParts.push(`cursor=${encodeURIComponent(params.cursor)}`);
    const qs = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
    return this.makeRequest(`/workflows/${workflowId}/executions${qs}`);
  }

  async getWorkflowExecution(workflowId: string, executionKey: string) {
    // executionKey = contactKey#<enteredAt> composite (URL-encoded in path)
    return this.makeRequest(`/workflows/${workflowId}/executions/${encodeURIComponent(executionKey)}`);
  }

  async abortWorkflowExecution(workflowId: string, executionKey: string) {
    return this.makeRequest(`/workflows/${workflowId}/executions/${encodeURIComponent(executionKey)}/abort`, {
      method: 'POST',
    });
  }

  async retryWorkflowExecution(workflowId: string, executionKey: string) {
    const retryRequestId = `wfr_${crypto.randomUUID().replace(/-/g, '')}`;
    return this.makeRequest(`/workflows/${workflowId}/executions/${encodeURIComponent(executionKey)}/retry`, {
      method: 'POST',
      body: JSON.stringify({ retryRequestId }),
    });
  }

  async createWorkflowRetryJob(workflowId: string, request: {
    filters?: { from?: string; to?: string; nodeId?: string; retryableOnly?: boolean };
    maxExecutions?: number;
  }) {
    return this.makeRequest(`/workflows/${workflowId}/retry-jobs`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async getWorkflowRetryJob(workflowId: string, jobId: string) {
    return this.makeRequest(`/workflows/${workflowId}/retry-jobs/${encodeURIComponent(jobId)}`);
  }

  async getLatestWorkflowRetryJob(workflowId: string) {
    return this.makeRequest(`/workflows/${workflowId}/retry-jobs`);
  }

  async controlWorkflowRetryJob(workflowId: string, jobId: string, action: 'pause' | 'resume' | 'cancel') {
    return this.makeRequest(`/workflows/${workflowId}/retry-jobs/${encodeURIComponent(jobId)}/${action}`, {
      method: 'POST',
    });
  }

  // ─── Contacts endpoints ──────────────────────────────────────────────────

  async getContacts(params?: { limit?: number; lastKey?: string; search?: string; status?: string }) {
    if (params?.search?.trim()) {
      const result = await this.makeRequest('/contacts/search', {
        method: 'POST',
        body: JSON.stringify({ search: params.search.trim(), limit: params.limit, cursor: params.lastKey }),
      });
      return { ...result, nextKey: result.nextCursor || undefined };
    }
    const cleanParams: Record<string, string> = {};
    if (params) {
      Object.entries(params).forEach(([key, val]) => {
        if (val !== undefined && val !== null) {
          cleanParams[key] = String(val);
        }
      });
    }
    const queryString = Object.keys(cleanParams).length > 0 ? `?${new URLSearchParams(cleanParams).toString()}` : '';
    return this.makeRequest(`/contacts${queryString}`);
  }

  async getContact(contactId: string) {
    return this.makeRequest(`/contacts/${encodeURIComponent(contactId)}`);
  }

  async createContact(contactData: any) {
    return this.makeRequest('/contacts', {
      method: 'POST',
      body: JSON.stringify(contactData),
    });
  }

  async updateContact(contactId: string, contactData: any) {
    return this.makeRequest(`/contacts/${encodeURIComponent(contactId)}`, {
      method: 'PUT',
      body: JSON.stringify(contactData),
    });
  }

  async upsertContact(emailOrIdentifier: string, contactData: any) {
    return this.updateContact(emailOrIdentifier, contactData);
  }

  async deleteContact(contactId: string) {
    return this.makeRequest(`/contacts/${encodeURIComponent(contactId)}`, {
      method: 'DELETE',
    });
  }

  async deleteContactsBatch(contactIds: string[]) {
    return this.makeRequest('/contacts/batch-delete', {
      method: 'POST',
      body: JSON.stringify({ contactIds }),
    });
  }

  async deleteAllContacts() {
    return this.makeRequest('/contacts/delete-all', {
      method: 'POST',
    });
  }

  // ─── Custom Contact Attributes endpoints ───────────────────────────────────

  async getContactAttributes() {
    return this.makeRequest('/contacts/custom-fields');
  }

  async createContactAttribute(key: string, payload: { label: string; type: 'text' | 'number' | 'boolean' | 'date' | 'geolocation'; description?: string }) {
    return this.makeRequest(`/contacts/custom-fields/${encodeURIComponent(key)}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  }

  async deleteContactAttribute(key: string) {
    return this.makeRequest(`/contacts/custom-fields/${encodeURIComponent(key)}`, {
      method: 'DELETE',
    });
  }

  // ─── Subscriptions endpoints ──────────────────────────────────────────────

  async getSubscriptions(subscriptionGroupId: string, params?: any) {
    const cleanParams: Record<string, string> = {};
    if (params) {
      Object.entries(params).forEach(([key, val]) => {
        if (val !== undefined && val !== null) {
          cleanParams[key] = String(val);
        }
      });
    }
    const queryString = Object.keys(cleanParams).length > 0 ? `?${new URLSearchParams(cleanParams).toString()}` : '';
    return this.makeRequest(`/contacts/subscription-groups/${subscriptionGroupId}/subscriptions${queryString}`);
  }

  async getSubscription(subscriptionGroupId: string, memberKey: string) {
    return this.makeRequest(`/contacts/subscription-groups/${subscriptionGroupId}/subscriptions/${encodeURIComponent(memberKey)}`);
  }

  async addSubscription(subscriptionGroupId: string, payload: any) {
    return this.makeRequest(`/contacts/subscription-groups/${subscriptionGroupId}/subscriptions`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async updateSubscription(subscriptionGroupId: string, memberKey: string, payload: any) {
    return this.makeRequest(`/contacts/subscription-groups/${subscriptionGroupId}/subscriptions/${encodeURIComponent(memberKey)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async deleteSubscription(subscriptionGroupId: string, memberKey: string) {
    return this.makeRequest(`/contacts/subscription-groups/${subscriptionGroupId}/subscriptions/${encodeURIComponent(memberKey)}`, {
      method: 'DELETE',
    });
  }

  async deleteAllSubscribers(subscriptionGroupId: string) {
    return this.makeRequest(`/contacts/subscription-groups/${subscriptionGroupId}/subscriptions`, {
      method: 'DELETE',
    });
  }
  // ─── Segments endpoints ──────────────────────────────────────────────────

  async getSegments(params?: { limit?: number; lastKey?: string }) {
    const cleanParams: Record<string, string> = {};
    if (params) {
      Object.entries(params).forEach(([key, val]) => {
        if (val !== undefined && val !== null) {
          cleanParams[key] = String(val);
        }
      });
    }
    const queryString = Object.keys(cleanParams).length > 0 ? `?${new URLSearchParams(cleanParams).toString()}` : '';
    return this.makeRequest(`/segments${queryString}`);
  }

  async getSegment(segmentId: string) {
    return this.makeRequest(`/segments/${segmentId}`);
  }

  async createSegment(segmentData: any) {
    return this.makeRequest('/segments', {
      method: 'POST',
      body: JSON.stringify(segmentData),
    });
  }

  async updateSegment(segmentId: string, segmentData: any) {
    return this.makeRequest(`/segments/${segmentId}`, {
      method: 'PUT',
      body: JSON.stringify(segmentData),
    });
  }

  async deleteSegment(segmentId: string) {
    return this.makeRequest(`/segments/${segmentId}`, {
      method: 'DELETE',
    });
  }

  async recalculateSegmentCount(segmentId: string) {
    return this.makeRequest(`/segments/${segmentId}/recalculate`, {
      method: 'POST',
    });
  }

  // ─── Teams endpoints ─────────────────────────────────────────────────────

  async getTeams(): Promise<{ workspaces: TeamWorkspace[] }> {
    return this.makeRequest('/teams');
  }

  async getTeamMembers(workspaceId: string): Promise<{ members: TeamMember[]; archivedMembers: TeamMember[]; invitations: TeamInvitation[] }> {
    return this.makeRequest(`/teams/${workspaceId}/members`);
  }

  async getAnalyticsPiiPolicy(workspaceId: string): Promise<{ policy: AnalyticsPiiPolicy }> {
    return this.makeRequest(`/teams/${workspaceId}/analytics-pii-policy`);
  }

  async updateAnalyticsPiiPolicy(workspaceId: string, policy: Pick<AnalyticsPiiPolicy, 'recipientAllowlist' | 'senderDomainAllowlist'>): Promise<{ policy: AnalyticsPiiPolicy }> {
    return this.makeRequest(`/teams/${workspaceId}/analytics-pii-policy`, {
      method: 'PUT',
      body: JSON.stringify(policy),
    });
  }

  async deleteAnalyticsPiiPolicy(workspaceId: string): Promise<{ success: boolean }> {
    return this.makeRequest(`/teams/${workspaceId}/analytics-pii-policy`, { method: 'DELETE' });
  }

  async inviteTeamMember(
    workspaceId: string,
    data: { email: string; role: string; mailboxAddress?: string },
  ): Promise<{ invitation: TeamInvitation; deliveryStatus?: 'sent' | 'failed'; warning?: string }> {
    return this.makeRequest(`/teams/${workspaceId}/invitations`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateTeamMember(workspaceId: string, userId: string, data: { role: string; permissions?: string[] }): Promise<{ member: TeamMember }> {
    return this.makeRequest(`/teams/${workspaceId}/members/${userId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async removeTeamMember(workspaceId: string, userId: string): Promise<{ success: boolean }> {
    return this.makeRequest(`/teams/${workspaceId}/members/${userId}`, {
      method: 'DELETE',
    });
  }

  async reinstateTeamMember(workspaceId: string, userId: string, data: { role: string }): Promise<{ member: TeamMember; deliveryStatus: 'sent' | 'failed'; warning?: string }> {
    return this.makeRequest(`/teams/${workspaceId}/members/${userId}/reinstate`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getMyTeamInvitations(): Promise<{ invitations: TeamInvitation[] }> {
    return this.makeRequest('/teams/invitations');
  }

  async acceptTeamInvitation(workspaceId: string, invitationId: string): Promise<{ success: boolean }> {
    return this.makeRequest('/teams/invitations/accept', {
      method: 'POST',
      body: JSON.stringify({ workspaceId, invitationId }),
    });
  }

  async declineTeamInvitation(workspaceId: string, invitationId: string): Promise<{ success: boolean }> {
    return this.makeRequest('/teams/invitations/decline', {
      method: 'POST',
      body: JSON.stringify({ workspaceId, invitationId }),
    });
  }

  async resendTeamInvitation(workspaceId: string, invitationId: string): Promise<{ invitation: TeamInvitation }> {
    return this.makeRequest(`/teams/${workspaceId}/invitations/${invitationId}/resend`, {
      method: 'POST',
    });
  }

  async cancelTeamInvitation(workspaceId: string, invitationId: string): Promise<{ success: boolean }> {
    return this.makeRequest(`/teams/${workspaceId}/invitations/${invitationId}`, {
      method: 'DELETE',
    });
  }

  // ─── Mailbox management endpoints ────────────────────────────────────────

  async getAssignedMailboxDomains(workspaceId?: string): Promise<{ domains: Array<Pick<Domain, 'domainId' | 'domain' | 'status' | 'useCase' | 'inboxSubdomain' | 'inboxEnabled' | 'inboxVerified'>> }> {
    return this.makeRequest('/web-setup/assigned-mailbox-domains', {}, workspaceId);
  }

  async getEligibleMailboxDomains(workspaceId?: string): Promise<{ domains: Array<{ domainId: string; domain: string; useCase?: string; canReceive: boolean; receiveDomain: string }> }> {
    return this.makeRequest('/web-setup/mailboxes/eligible-domains', {}, workspaceId);
  }

  async getMailboxConfiguration(workspaceId?: string): ReturnType<typeof this.getMailboxes> {
    return this.makeRequest('/web-setup/mailboxes', {}, workspaceId);
  }

  async getMailboxes(workspaceId?: string): Promise<{
    mailboxes: Array<{
      emailAddress: string;
      receiveAddress: string;
      sendAsAddress: string;
      domainId: string;
      useCase?: 'tx' | 'mk';
      mailboxType: 'personal' | 'shared';
      receivesEmail: boolean;
      senderName?: string;
      pendingAccessCount: number;
      hasAccess: boolean;
      canDeleteAllTrash: boolean;
      access: Array<{ userId: string; grantedBy: string; createdAt: string }>;
    }>;
    mailboxDomainIds?: string[];
    members: Array<{ userId: string; email: string; name: string }>;
    canManage: boolean;
    maxUsersPerSharedMailbox: number;
    maxMailboxes: number;
  }> {
    return this.makeRequest('/web-setup/assigned-mailboxes', {}, workspaceId);
  }

  async getAssignedSenderIdentities(useCase?: 'mk', workspaceId?: string): Promise<{
    identities: Array<{
      emailAddress: string;
      receiveAddress: string;
      sendAsAddress: string;
      domainId: string;
      useCase: 'tx' | 'mk';
      mailboxType: 'personal' | 'shared';
      receivesEmail: boolean;
      senderName?: string;
    }>;
  }> {
    return this.makeRequest(`/web-setup/sender-identities${useCase ? `?useCase=${useCase}` : ''}`, {}, workspaceId);
  }

  async provisionMailbox(data: { emailAddress: string; mailboxType?: 'personal' | 'shared'; receivesEmail?: boolean; senderName?: string }): Promise<{
    mailbox: {
      emailAddress: string;
      receiveAddress: string;
      sendAsAddress: string;
      mailboxType: 'personal' | 'shared';
      receivesEmail: boolean;
      senderName?: string;
    };
  }> {
    return this.makeRequest('/web-setup/mailboxes', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async grantMailboxAccess(emailAddress: string, userId: string): Promise<{ success: boolean; access: unknown }> {
    return this.makeRequest('/web-setup/mailboxes/access/grant', {
      method: 'POST',
      body: JSON.stringify({ emailAddress, userId }),
    });
  }

  async revokeMailboxAccess(emailAddress: string, userId: string): Promise<{ success: boolean; revoked: unknown }> {
    return this.makeRequest('/web-setup/mailboxes/access/revoke', {
      method: 'POST',
      body: JSON.stringify({ emailAddress, userId }),
    });
  }

  async deleteMailbox(emailAddress: string): Promise<{
    success: boolean;
    deleted: { emailAddress: string; deleted: boolean; revokedAccessCount: number };
  }> {
    return this.makeRequest('/web-setup/mailboxes', {
      method: 'DELETE',
      body: JSON.stringify({ emailAddress }),
    });
  }

  // ============================================================
  // WORKSPACE MANAGEMENT
  // ============================================================

  async createWorkspace(data: { name: string; email?: string; parentWorkspaceId?: string; workspaceType?: 'live' | 'sandbox' }): Promise<{ workspace: Workspace }> {
    return this.makeRequest('/workspaces', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getWorkspaces(): Promise<{ workspaces: Workspace[] }> {
    return this.makeRequest('/workspaces');
  }

  async getWorkspace(workspaceId: string): Promise<{ workspace: Workspace }> {
    return this.makeRequest(`/workspaces/${workspaceId}`);
  }

  async deleteWorkspace(workspaceId: string, confirmationToken: string): Promise<{ success: boolean; message: string }> {
    return this.makeRequest(`/workspaces/${workspaceId}`, {
      method: 'DELETE',
      body: JSON.stringify({ confirmationToken }),
      headers: { 'x-workspace-id': workspaceId },
    });
  }

  async checkWorkspaceDeletion(workspaceId: string): Promise<{
    canDelete: boolean;
    reason: string | null;
    message: string;
    blockingTables?: Array<{ table: string; envVar: string; count: number; clearLink: string }>;
    memberCount?: number;
    childWorkspaceCount?: number;
  }> {
    return this.makeRequest(`/workspaces/${workspaceId}/deletion-check`, {
      headers: { 'x-workspace-id': workspaceId },
    });
  }

  async updateWorkspaceBranding(workspaceId: string, data: WorkspaceBranding): Promise<{ workspace: Workspace }> {
    return this.makeRequest(`/workspaces/${workspaceId}/branding`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async transferCredits(workspaceId: string, data: { targetWorkspaceId: string; amount: number; operationId: string }): Promise<{ success: boolean; message?: string }> {
    return this.makeRequest(`/workspaces/${workspaceId}/credit-transfer`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ============================================================
  // ROLES (DB-backed)
  // ============================================================

  async getWorkspaceRoles(workspaceId?: string): Promise<{ roles: WorkspaceRole[]; allPermissions: string[] }> {
    const wsId = workspaceId || this.currentWorkspaceId || 'default';
    return this.makeRequest(`/teams/${wsId}/roles`);
  }

  async createWorkspaceRole(workspaceId: string, data: { name: string; description?: string; permissions: string[]; principalTypes?: string[]; apiKeyTypes?: string[] }): Promise<{ role: WorkspaceRole }> {
    return this.makeRequest(`/teams/${workspaceId}/roles`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateWorkspaceRole(workspaceId: string, roleId: string, data: { name?: string; description?: string; permissions?: string[] }): Promise<{ role: WorkspaceRole }> {
    return this.makeRequest(`/teams/${workspaceId}/roles/${roleId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteWorkspaceRole(workspaceId: string, roleId: string): Promise<{ success: boolean }> {
    return this.makeRequest(`/teams/${workspaceId}/roles/${roleId}`, {
      method: 'DELETE',
    });
  }

  // ============================================================
  // ADDITIONAL PERMISSIONS (GRANT / REVOKE)
  // ============================================================

  async grantPermission(workspaceId: string, targetUserId: string, data: { permission: string; expiresAt?: string; reason?: string }): Promise<{ success: boolean; grant: AdditionalPermissionGrant }> {
    return this.makeRequest(`/teams/${workspaceId}/members/${targetUserId}/permissions`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async revokePermission(workspaceId: string, targetUserId: string, permission: string): Promise<{ success: boolean }> {
    return this.makeRequest(`/teams/${workspaceId}/members/${targetUserId}/permissions/${encodeURIComponent(permission)}`, {
      method: 'DELETE',
    });
  }

  async getMemberPermissions(workspaceId: string, targetUserId: string): Promise<{ additionalPermissions: AdditionalPermissionGrant[] }> {
    return this.makeRequest(`/teams/${workspaceId}/members/${targetUserId}/permissions`);
  }

  // ============================================================
  // AUDIT LOG
  // ============================================================

  async getAuditLog(workspaceId: string, params?: { startDate?: string; endDate?: string; action?: string; targetUserId?: string; limit?: number; nextToken?: string }): Promise<{ items: AuditLogEntry[]; nextToken: string | null }> {
    const queryParams = new URLSearchParams();
    if (params?.startDate) queryParams.set('startDate', params.startDate);
    if (params?.endDate) queryParams.set('endDate', params.endDate);
    if (params?.action) queryParams.set('action', params.action);
    if (params?.targetUserId) queryParams.set('targetUserId', params.targetUserId);
    if (params?.limit) queryParams.set('limit', params.limit.toString());
    if (params?.nextToken) queryParams.set('nextToken', params.nextToken);
    const qs = queryParams.toString();
    return this.makeRequest(`/teams/${workspaceId}/audit-log${qs ? `?${qs}` : ''}`);
  }
}

// Export the service instance
export const apiService = new ApiService();
export default apiService;

function md5(str: string): string {
  const k: number[] = [];
  for (let i = 0; i < 64;) {
    k[i] = 0 | (Math.abs(Math.sin(++i)) * 4294967296);
  }
  let h0 = 0x67452301, h1 = 0xEFCDAB89, h2 = 0x98BADCFE, h3 = 0x10325476;
  const s = [
    7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22, 7, 12, 17, 22,
    5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20, 5, 9, 14, 20,
    4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23, 4, 11, 16, 23,
    6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21, 6, 10, 15, 21
  ];
  str += String.fromCharCode(0x80);
  const words = [0];
  for (let i = 0; i < str.length; i++) {
    words[i >> 2] |= str.charCodeAt(i) << ((i % 4) * 8);
  }
  words[(((str.length + 8) >> 6) << 4) + 14] = (str.length - 1) * 8;
  for (let i = 0; i < words.length; i += 16) {
    let a = h0, b = h1, c = h2, d = h3;
    for (let j = 0; j < 64; j++) {
      let f, g;
      if (j < 16) {
        f = (b & c) | (~b & d); g = j;
      } else if (j < 32) {
        f = (d & b) | (~d & c); g = (5 * j + 1) % 16;
      } else if (j < 48) {
        f = b ^ c ^ d; g = (3 * j + 5) % 16;
      } else {
        f = c ^ (b | ~d); g = (7 * j) % 16;
      }
      const temp = d;
      d = c;
      c = b;
      b = (b + rotateLeft((a + f + k[j] + (words[i + g] || 0)), s[j])) | 0;
      a = temp;
    }
    h0 = (h0 + a) | 0;
    h1 = (h1 + b) | 0;
    h2 = (h2 + c) | 0;
    h3 = (h3 + d) | 0;
  }
  return (hex(h0) + hex(h1) + hex(h2) + hex(h3)).toLowerCase();

  function rotateLeft(q: number, w: number) {
    return (q << w) | (q >>> (32 - w));
  }
  function hex(n: number) {
    let output = '';
    for (let i = 0; i < 4; i++) {
      output += ((n >>> (i * 8)) & 0xff).toString(16).padStart(2, '0');
    }
    return output;
  }
}

export function getContactSortKey(channel: 'email' | 'phone', value: string): string {
  if (!value) return '';
  let normalized = value.trim().toLowerCase();
  if (channel === 'phone') {
    normalized = normalized.replace(/[\s().-]/g, '');
  }
  return `${channel}_${md5(normalized)}`;
}

export function resolveIdentifier(value: string): string {
  if (!value) return '';
  const trimmed = value.trim();
  if (trimmed.startsWith('email_') || trimmed.startsWith('phone_')) {
    return trimmed;
  }
  if (trimmed.includes('@')) {
    return getContactSortKey('email', trimmed);
  }
  return getContactSortKey('phone', trimmed);
}
